import { useState } from 'react';
import type { ComponentType } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ShippingWindow from '#features/product/Edit.ShippingWindow';
import Customization from '#features/product/Edit.Customization';
import {
  EDIT_PANEL_SCOPE,
  EDIT_PANEL_EVENT_TYPE,
} from '#common/constants/addons';

export interface EditPanelChange {
  name?: string;
  touchedName?: string;
  value?: unknown;
}

export interface EditPanelProps {
  onChange: (event: unknown, change: EditPanelChange) => void;
  errors?: Record<string, unknown>;
  errorMessages?: Record<string, unknown>;
}

type BatchRow = Record<string, string>;

function EditPanelTitle({ onChange }: EditPanelProps) {
  const [editType, setEditType] = useState<string>(EDIT_PANEL_EVENT_TYPE.BEGINNING);
  const [titleTextBeginning, setTitleTextBeginning] = useState('');
  const [titleTextEnd, setTitleTextEnd] = useState('');
  const [titleFind, setTitleFind] = useState('');
  const [titleReplace, setTitleReplace] = useState('');

  const handleChange = (e: unknown, type?: string | null, updates?: Record<string, unknown>) => {
    let data: Record<string, unknown> | null = null;
    const currentEditType = type || editType;
    if (EDIT_PANEL_EVENT_TYPE.BEGINNING === currentEditType) {
      data = {
        text: titleTextBeginning,
      };
    } else if (EDIT_PANEL_EVENT_TYPE.END === currentEditType) {
      data = {
        text: titleTextEnd,
      };
    } else if (EDIT_PANEL_EVENT_TYPE.REPLACE === currentEditType) {
      data = {
        find: titleFind,
        replace: titleReplace,
      };
    }
    data = {
      ...data,
      ...updates,
    };

    onChange(e, {
      name: 'diff',
      value: {
        data,
        event: 'set',
        eventType: currentEditType,
        scope: EDIT_PANEL_SCOPE.TITLE,
      },
    });
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Title
      </Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ my: 2 }}
      >
        <FormControl sx={{ width: 200 }} size="small">
          <InputLabel>Edit type</InputLabel>
          <Select
            value={editType}
            label="Edit type"
            onChange={(e) => {
              const newEditType = e.target.value;
              setEditType(newEditType);
              handleChange(e, newEditType);
            }}
          >
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.BEGINNING}>Insert at beginning</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.END}>Insert at end</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.REPLACE}>Replace all</MenuItem>
          </Select>
        </FormControl>
        {
          (editType === EDIT_PANEL_EVENT_TYPE.REPLACE) && (
            <>
              <TextField
                sx={{ flex: 1 }}
                label="Find"
                rows={4}
                size="small"
                placeholder="Find"
                value={titleFind}
                onChange={(e) => {
                  const newTitleFind = e.target.value;
                  setTitleFind(newTitleFind);
                  handleChange(e, null, {
                    find: newTitleFind,
                  });
                }}
              />
              <TextField
                sx={{ flex: 1 }}
                label="Replace"
                rows={4}
                size="small"
                placeholder="Replace"
                value={titleReplace}
                onChange={(e) => {
                  const newTitleReplace = e.target.value;
                  setTitleReplace(newTitleReplace);
                  handleChange(e, null, {
                    replace: newTitleReplace,
                  });
                }}
              />
            </>
          )
        }
        {
          (editType === EDIT_PANEL_EVENT_TYPE.BEGINNING) && (
            <TextField
              sx={{ flex: 1 }}
              label="Text"
              rows={4}
              size="small"
              placeholder="Add"
              value={titleTextBeginning}
              onChange={(e) => {
                const newTitle = e.target.value;
                setTitleTextBeginning(newTitle);
                handleChange(e, null, {
                  text: newTitle,
                });
              }}
            />
          )
        }
        {
          (editType === EDIT_PANEL_EVENT_TYPE.END) && (
            <TextField
              sx={{ flex: 1 }}
              label="Text"
              rows={4}
              size="small"
              placeholder="Add"
              value={titleTextEnd}
              onChange={(e) => {
                const newTitle = e.target.value;
                setTitleTextEnd(newTitle);
                handleChange(e, null, {
                  text: newTitle,
                });
              }}
            />
          )
        }
      </Stack>
    </>
  );
}

function EditPanelDescription({ onChange }: EditPanelProps) {
  const [editType, setEditType] = useState<string>(EDIT_PANEL_EVENT_TYPE.BEGINNING);
  const [descriptionBeginning, setDescriptionBeginning] = useState('');
  const [descriptionEnd, setDescriptionEnd] = useState('');
  const [descriptionFind, setDescriptionFind] = useState('');
  const [descriptionReplace, setDescriptionReplace] = useState('');

  const handleChange = (e: unknown, type?: string | null, updates?: Record<string, unknown>) => {
    let data: Record<string, unknown> | null = null;
    const currentEditType = type || editType;
    if (EDIT_PANEL_EVENT_TYPE.BEGINNING === currentEditType) {
      data = {
        text: descriptionBeginning,
      };
    } else if (EDIT_PANEL_EVENT_TYPE.END === currentEditType) {
      data = {
        text: descriptionEnd,
      };
    } else if (EDIT_PANEL_EVENT_TYPE.REPLACE === currentEditType) {
      data = {
        find: descriptionFind,
        replace: descriptionReplace,
      };
    }
    data = {
      ...data,
      ...updates,
    };

    onChange(e, {
      name: 'diff',
      value: {
        data,
        event: 'set',
        eventType: currentEditType,
        scope: EDIT_PANEL_SCOPE.DESCRIPTION,
      },
    });
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Description
      </Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ my: 2 }}
      >
        <FormControl sx={{ width: 200 }} size="small">
          <InputLabel>Edit type</InputLabel>
          <Select
            value={editType}
            label="Edit type"
            onChange={(e) => {
              const newEditType = e.target.value;
              setEditType(newEditType);
              handleChange(e, newEditType);
            }}
          >
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.BEGINNING}>Insert at beginning</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.END}>Insert at end</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.REPLACE}>Replace all</MenuItem>
          </Select>
        </FormControl>
        {
          (editType === EDIT_PANEL_EVENT_TYPE.REPLACE) && (
            <>
              <TextField
                sx={{ flex: 1 }}
                label="Find"
                multiline
                rows={4}
                size="small"
                placeholder="Find"
                value={descriptionFind}
                onChange={(e) => {
                  const newDescriptionFind = e.target.value;
                  setDescriptionFind(newDescriptionFind);
                  handleChange(e, null, {
                    find: newDescriptionFind,
                  });
                }}
              />
              <TextField
                sx={{ flex: 1 }}
                label="Replace"
                multiline
                rows={4}
                size="small"
                placeholder="Replace"
                value={descriptionReplace}
                onChange={(e) => {
                  const newDescriptionReplace = e.target.value;
                  setDescriptionReplace(newDescriptionReplace);
                  handleChange(e, null, {
                    replace: newDescriptionReplace,
                  });
                }}
              />
            </>
          )
        }
        {
          (editType === EDIT_PANEL_EVENT_TYPE.BEGINNING) && (
            <TextField
              sx={{ flex: 1 }}
              label="Text"
              multiline
              rows={4}
              placeholder="Add"
              value={descriptionBeginning}
              onChange={(e) => {
                const newDescription = e.target.value;
                setDescriptionBeginning(newDescription);
                handleChange(e, null, {
                  text: newDescription,
                });
              }}
            />
          )
        }
        {
          (editType === EDIT_PANEL_EVENT_TYPE.END) && (
            <TextField
              sx={{ flex: 1 }}
              label="Text"
              multiline
              rows={4}
              placeholder="Add"
              value={descriptionEnd}
              onChange={(e) => {
                const newDescription = e.target.value;
                setDescriptionEnd(newDescription);
                handleChange(e, null, {
                  text: newDescription,
                });
              }}
            />
          )
        }
      </Stack>
    </>
  );
}

function EditPanelShippingWindow({ onChange, errors = {}, errorMessages = {} }: EditPanelProps) {
  const [editType, setEditType] = useState<string>(EDIT_PANEL_EVENT_TYPE.ADD);
  const [shippingWindow, setShippingWindow] = useState<BatchRow[]>([{ name: '', weeks: '', price: '' }]);

  const buildDiff = (type: string, value: unknown) => {
    return {
      data: value,
      event: type,
      scope: EDIT_PANEL_SCOPE.SHIPPING_WINDOW,
    };
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Shipping Window
      </Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ my: 2 }}
      >
        <FormControl sx={{ width: 200 }} size="small">
          <InputLabel>Edit type</InputLabel>
          <Select
            value={editType}
            label="Edit type"
            onChange={(e) => {
              setEditType(e.target.value);
              setShippingWindow([{ name: '', weeks: '', price: '' }]);
              onChange(e, {
                name: 'diff',
                touchedName: 'shippingWindow',
                value: buildDiff(e.target.value, [{ name: '', weeks: '', price: '' }]),
              });
            }}
          >
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.ADD}>Add</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.REMOVE}>Remove</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.REPLACE}>Replace all</MenuItem>
          </Select>
        </FormControl>

        <Box>
          <ShippingWindow
            name="shippingWindow"
            value={shippingWindow}
            sizes={editType === EDIT_PANEL_EVENT_TYPE.REMOVE ? [10] : [4, 3, 3]}
            editType={editType}
            errors={errors.shippingWindow as Parameters<typeof ShippingWindow>[0]['errors']}
            errorMessages={errorMessages.shippingWindow as Parameters<typeof ShippingWindow>[0]['errorMessages']}
            controlVisibility={
              (editType === EDIT_PANEL_EVENT_TYPE.REMOVE)
                ? ['name']
                : undefined
            }
            onChange={(e, changes) => {
              setShippingWindow([...changes.value]);
              onChange(e, {
                name: 'diff',
                touchedName: 'shippingWindow',
                value: buildDiff(editType, changes.value),
              });
            }}
          />
        </Box>
      </Stack>
    </>
  );
}

function EditPanelCustomization({ onChange, errors = {}, errorMessages = {} }: EditPanelProps) {
  const [editType, setEditType] = useState<string>(EDIT_PANEL_EVENT_TYPE.ADD);
  const [customization, setCustomization] = useState<BatchRow[]>([{ name: '', price: '' }]);

  const buildDiff = (type: string, value: unknown) => {
    return {
      data: value,
      event: type,
      scope: EDIT_PANEL_SCOPE.CUSTOMIZATION,
    };
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Customization
      </Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ my: 2 }}
      >
        <FormControl sx={{ width: 200 }} size="small">
          <InputLabel>Edit type</InputLabel>
          <Select
            value={editType}
            label="Edit type"
            onChange={(e) => {
              const newEditType = e.target.value;
              setEditType(newEditType);
              setCustomization([{ name: '', price: '' }]);
              onChange(e, {
                name: 'diff',
                touchedName: 'customization',
                value: buildDiff(newEditType, [{ name: '', price: '' }]),
              });
            }}
          >
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.ADD}>Add</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.REMOVE}>Remove</MenuItem>
            <MenuItem value={EDIT_PANEL_EVENT_TYPE.REPLACE}>Replace all</MenuItem>
          </Select>
        </FormControl>

        <Box>
          <Customization
            name="customization"
            value={customization}
            sizes={editType === EDIT_PANEL_EVENT_TYPE.REMOVE ? [10] : [7, 3]}
            errors={errors.customization as Parameters<typeof Customization>[0]['errors']}
            errorMessages={errorMessages.customization as Parameters<typeof Customization>[0]['errorMessages']}
            editType={editType}
            controlVisibility={
              (editType === EDIT_PANEL_EVENT_TYPE.REMOVE)
                ? ['name']
                : undefined
            }
            onChange={(e, changes) => {
              setCustomization([...changes.value]);
              onChange(e, {
                name: 'diff',
                touchedName: 'customization',
                value: buildDiff(editType, changes.value),
              });
            }}
          />
        </Box>
      </Stack>
    </>
  );
}

export const EDIT_PANEL_MAPPING: Record<string, ComponentType<EditPanelProps>> = {
  title: EditPanelTitle,
  description: EditPanelDescription,
  shippingWindow: EditPanelShippingWindow,
  customization: EditPanelCustomization,
};
